import { useState, useEffect, useMemo } from 'react'
import { fetchAuditLog, fetchSubtaskDatesByProject, fetchWorkflowSnapshots } from '../lib/api'
import { businessDaysBetween, isOffDay } from '../lib/businessDays'

function fmtDateTime(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  })
}

function fmtDate(d) {
  if (!d) return '—'
  const [y, m, day] = String(d).split('-')
  return new Date(+y, +m - 1, +day).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

const ACTION_STYLE = {
  created:  { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Created' },
  updated:  { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Updated' },
  deleted:  { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Deleted' },
  restored: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Restored' },
}

function daysDelta(from, to) {
  if (!from || !to) return null
  const parseD = s => { const [y, m, d] = String(s).split('-').map(Number); return new Date(y, m - 1, d) }
  return Math.round((parseD(to) - parseD(from)) / 86400000)
}

// Inclusive business-day span: a task that starts and ends on the same business day = 1.
function businessDaySpan(fromIso, toIso) {
  if (!fromIso || !toIso) return null
  const startIsBusinessDay = !isOffDay(fromIso)
  return businessDaysBetween(fromIso, toIso) + (startIsBusinessDay ? 1 : 0)
}

function buildProjectDateSummary(entries, departure_date_baseline, arrival_date_baseline) {
  const arrivalChanges   = []
  const departureChanges = []
  let inWorkAt = null

  for (const e of entries) {
    if (e.entity_type !== 'project' || e.action !== 'updated' || !e.changed_fields?.length) continue
    for (const cf of e.changed_fields) {
      if (cf.field === 'Arrival Date')   arrivalChanges.push({ from: cf.from, to: cf.to, at: e.created_at })
      if (cf.field === 'Departure Date') departureChanges.push({ from: cf.from, to: cf.to, at: e.created_at })
      if (cf.field === 'Status' && cf.to === 'In Work' && !inWorkAt) inWorkAt = e.created_at
    }
  }

  if (!arrivalChanges.length && !departureChanges.length) return null

  arrivalChanges.sort((a, b)   => new Date(a.at) - new Date(b.at))
  departureChanges.sort((a, b) => new Date(a.at) - new Date(b.at))

  let initialArrival, initialDelivery

  if (inWorkAt) {
    const firstArrAfter = arrivalChanges.find(c => c.at > inWorkAt)
    const firstDepAfter = departureChanges.find(c => c.at > inWorkAt)
    initialArrival  = arrival_date_baseline ?? firstArrAfter?.from ?? null
    initialDelivery = departure_date_baseline ?? firstDepAfter?.from ?? null
  } else {
    initialArrival  = arrival_date_baseline ?? arrivalChanges[0]?.from  ?? null
    initialDelivery = departure_date_baseline ?? departureChanges[0]?.from ?? null
  }

  // If one side had no recorded changes, its final value equals the initial (unchanged).
  const finalArrival  = arrivalChanges.at(-1)?.to  ?? initialArrival  ?? null
  const finalDelivery = departureChanges.at(-1)?.to ?? initialDelivery ?? null

  return { initialArrival, finalArrival, initialDelivery, finalDelivery }
}

// Shared helper: collects all start/end date changes per subtask from audit log.
// currentDates: Map<id, { start_date, end_date }> from the DB — used as fallback
// when one side never appeared in the audit log (unchanged field).
// inWorkAt: when provided, the "initial" values are anchored to the state of dates
// at that timestamp rather than the very first ever recorded value.
function buildSubtaskDateChanges(entries, currentDates = new Map(), inWorkAt = null) {
  const bySubtask = new Map()

  for (const entry of entries) {
    if (entry.entity_type !== 'subtask' || !entry.changed_fields?.length) continue
    if (!bySubtask.has(entry.entity_id)) {
      bySubtask.set(entry.entity_id, { label: entry.entity_label, startChanges: [], endChanges: [], createdStart: null })
    }
    const sub = bySubtask.get(entry.entity_id)
    for (const cf of entry.changed_fields) {
      if (entry.action === 'updated') {
        if (cf.field === 'Start Date') sub.startChanges.push({ from: cf.from, to: cf.to, at: entry.created_at })
        if (cf.field === 'End Date')   sub.endChanges.push({ from: cf.from, to: cf.to, at: entry.created_at })
      }
      if (cf.field === 'Start Date' && cf.to && !sub.createdStart) sub.createdStart = cf.to
    }
  }

  // Also include subtasks that have current DB dates but may not appear in audit entries
  for (const [id, cur] of currentDates) {
    if (!bySubtask.has(id)) {
      bySubtask.set(id, { label: null, startChanges: [], endChanges: [], createdStart: null })
    }
  }

  const results = []
  for (const [id, sub] of bySubtask) {
    sub.startChanges.sort((a, b) => new Date(a.at) - new Date(b.at))
    sub.endChanges.sort((a, b)   => new Date(a.at) - new Date(b.at))

    const cur = currentDates.get(id)

    let initArr, initDel
    if (inWorkAt) {
      // Anchor initial values to the state of dates at the moment the project entered "In Work".
      // Use the last pre-inWork change's "to" value; fall back to the first-ever "from" if no
      // changes existed yet, then to the current DB date if there's no audit history at all.
      const lastStartBefore = sub.startChanges.filter(c => c.at <= inWorkAt).at(-1)
      const lastEndBefore   = sub.endChanges.filter(c => c.at <= inWorkAt).at(-1)
      initArr = lastStartBefore?.to ?? sub.startChanges[0]?.from ?? cur?.start_date ?? null
      initDel = lastEndBefore?.to   ?? sub.endChanges[0]?.from   ?? cur?.end_date   ?? null
    } else {
      initArr = sub.startChanges[0]?.from ?? cur?.start_date ?? null
      initDel = sub.endChanges[0]?.from   ?? cur?.end_date   ?? null
    }

    const finalArr = sub.startChanges.at(-1)?.to ?? cur?.start_date ?? null
    const finalDel = sub.endChanges.at(-1)?.to ?? cur?.end_date ?? null
    const staticStart = finalArr ?? sub.createdStart ?? null

    results.push({ id, label: sub.label, initArr, finalArr, initDel, finalDel, staticStart })
  }
  return results
}

function buildDelays(entries, currentDates) {
  const changes = buildSubtaskDateChanges(entries, currentDates)
  const delays = []
  for (const s of changes) {
    const arrDelayed = s.initArr && s.finalArr && s.finalArr > s.initArr
    const delDelayed = s.initDel && s.finalDel && s.finalDel > s.initDel
    if (!arrDelayed && !delDelayed) continue
    const staticDel = s.finalDel ?? s.initDel
    delays.push({
      id:              s.id,
      label:           s.label,
      // Always include arrival: use actual change values if delayed, otherwise show static date
      initialArrival:  arrDelayed ? s.initArr  : s.staticStart,
      finalArrival:    arrDelayed ? s.finalArr : s.staticStart,
      arrChanged:      arrDelayed,
      // When delivery didn't move, show the current date (finalDel) rather than the original
      // to avoid '—' when the first audit entry had no prior date recorded.
      initialDelivery: delDelayed ? s.initDel : staticDel,
      finalDelivery:   delDelayed ? s.finalDel : staticDel,
      delChanged:      delDelayed,
    })
  }
  delays.sort((a, b) => {
    if (!a.finalDelivery && !b.finalDelivery) return 0
    if (!a.finalDelivery) return 1
    if (!b.finalDelivery) return -1
    return a.finalDelivery < b.finalDelivery ? -1 : a.finalDelivery > b.finalDelivery ? 1 : 0
  })
  return delays
}

function buildImprovements(entries, currentDates) {
  const changes = buildSubtaskDateChanges(entries, currentDates)
  const improvements = []
  for (const s of changes) {
    const arrImproved = s.initArr && s.finalArr && s.finalArr < s.initArr
    const delImproved = s.initDel && s.finalDel && s.finalDel < s.initDel
    if (!arrImproved && !delImproved) continue
    const staticDel = s.finalDel ?? s.initDel
    improvements.push({
      id:              s.id,
      label:           s.label,
      initialArrival:  arrImproved ? s.initArr  : s.staticStart,
      finalArrival:    arrImproved ? s.finalArr : s.staticStart,
      arrChanged:      arrImproved,
      initialDelivery: delImproved ? s.initDel : staticDel,
      finalDelivery:   delImproved ? s.finalDel : staticDel,
      delChanged:      delImproved,
    })
  }
  improvements.sort((a, b) => {
    if (!a.finalDelivery && !b.finalDelivery) return 0
    if (!a.finalDelivery) return 1
    if (!b.finalDelivery) return -1
    return a.finalDelivery < b.finalDelivery ? -1 : a.finalDelivery > b.finalDelivery ? 1 : 0
  })
  return improvements
}

function buildDowntime(entries, currentDates) {
  let inWorkAt = null
  for (const e of entries) {
    if (e.entity_type !== 'project' || e.action !== 'updated' || !e.changed_fields?.length) continue
    for (const cf of e.changed_fields) {
      if (cf.field === 'Status' && cf.to === 'In Work' && !inWorkAt) inWorkAt = e.created_at
    }
  }
  const changes = buildSubtaskDateChanges(entries, currentDates, inWorkAt)
  const result = []
  for (const s of changes) {
    const initStart = s.initArr ?? s.staticStart
    const finalStart = s.finalArr ?? s.staticStart
    const initEnd  = s.initDel
    const finalEnd = s.finalDel

    if (!initStart || !finalStart || !initEnd || !finalEnd) continue

    const initialBD = businessDaySpan(initStart, initEnd)
    const finalBD   = businessDaySpan(finalStart, finalEnd)

    if (initialBD === finalBD) continue

    result.push({ id: s.id, label: s.label, initialDays: initialBD, finalDays: finalBD })
  }
  result.sort((a, b) => Math.abs(b.finalDays - b.initialDays) - Math.abs(a.finalDays - a.initialDays))
  return result
}

function ArrowRight() {
  return (
    <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  )
}

function DateChangeRow({ label, initial, final, changed, accentClass }) {
  const delta = changed ? daysDelta(initial, final) : null
  return (
    <div>
      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</div>
      {changed ? (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs px-1.5 py-0.5 bg-red-50 text-red-500 rounded line-through">{fmtDate(initial)}</span>
          <ArrowRight />
          <span className="text-xs px-1.5 py-0.5 bg-green-50 text-green-700 rounded">{fmtDate(final)}</span>
          {delta != null && delta !== 0 && (
            <span className={`text-[10px] font-bold ${accentClass}`}>{delta > 0 ? `+${delta}d` : `${delta}d`}</span>
          )}
        </div>
      ) : (
        <div className="text-xs text-gray-600">{fmtDate(initial)}</div>
      )}
    </div>
  )
}

function SubtaskDateCard({ item, accent, bg, divider, arrLabel = 'Start', delLabel = 'End' }) {
  const hasDelivery = item.initialDelivery || item.finalDelivery
  return (
    <div key={item.id} className="px-5 py-4">
      <div className="text-sm font-medium text-gray-800 mb-3">{item.label}</div>
      <div className={`${bg} rounded-lg p-3 space-y-2.5`}>
        <DateChangeRow
          label={arrLabel}
          initial={item.initialArrival}
          final={item.finalArrival}
          changed={item.arrChanged}
          accentClass={accent}
        />
        {hasDelivery && (
          <>
            <div className={`border-t border-dashed ${divider}`} />
            <DateChangeRow
              label={delLabel}
              initial={item.initialDelivery}
              final={item.finalDelivery}
              changed={item.delChanged}
              accentClass={accent}
            />
          </>
        )}
      </div>
    </div>
  )
}

function fmtSnapshotDate(iso) {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return `${m}.${d}.${y.slice(2)}`
}

export default function ProjectHistoryDrawer({ projectId, projectLabel, departure_date_baseline, arrival_date_baseline, departure_date, arrival_date, onClose, onPrintSnapshot, subtaskId, subtaskLabel, subtaskStartDate, subtaskEndDate }) {
  const [entries, setEntries]         = useState([])
  const [currentDates, setCurrentDates] = useState(new Map())
  const [loading, setLoading]         = useState(true)
  const [activeTab, setActiveTab]     = useState('history')
  const [snapshots, setSnapshots]     = useState([])

  useEffect(() => {
    if (!projectId) return
    const fetches = [
      fetchAuditLog(projectId),
      fetchSubtaskDatesByProject(projectId),
      ...(subtaskId ? [] : [fetchWorkflowSnapshots(projectId)]),
    ]
    Promise.all(fetches).then(([{ data: log }, { data: subs }, snapsRes]) => {
      setEntries(log || [])
      const map = subtaskId
        ? new Map([[subtaskId, { start_date: subtaskStartDate, end_date: subtaskEndDate }]])
        : new Map((subs || []).map(s => [s.id, { start_date: s.start_date, end_date: s.end_date }]))
      setCurrentDates(map)
      setSnapshots(snapsRes?.data || [])
      setLoading(false)
    })
  }, [projectId, subtaskId, subtaskStartDate, subtaskEndDate])

  const visibleEntries = useMemo(() =>
    subtaskId
      ? entries.filter(e => e.entity_type === 'subtask' && e.entity_id === subtaskId)
      : entries,
    [entries, subtaskId]
  )

  const delays       = useMemo(() => buildDelays(visibleEntries, currentDates),       [visibleEntries, currentDates])
  const improvements = useMemo(() => buildImprovements(visibleEntries, currentDates), [visibleEntries, currentDates])
  const downtime     = useMemo(() => buildDowntime(visibleEntries, currentDates),     [visibleEntries, currentDates])
  const projectDowntimeSummary = useMemo(() => {
    if (subtaskId || !arrival_date_baseline || !departure_date_baseline) return null
    const initialDays = businessDaySpan(arrival_date_baseline, departure_date_baseline)
    const finalDays   = businessDaySpan(arrival_date ?? arrival_date_baseline, departure_date ?? departure_date_baseline)
    return { initialDays, finalDays, delta: finalDays - initialDays }
  }, [arrival_date_baseline, departure_date_baseline, arrival_date, departure_date])
  const projectDateSummary = useMemo(
    () => subtaskId ? null : buildProjectDateSummary(entries, departure_date_baseline, arrival_date_baseline),
    [entries, departure_date_baseline, arrival_date_baseline, subtaskId]
  )

  const registration = snapshots[0]?.state?.project?.aircraft?.registration || projectLabel

  const TABS = [
    { id: 'history',      label: 'History',      count: null,                badgeClass: null },
    { id: 'downtime',     label: 'Downtime',      count: downtime.length,     badgeClass: 'bg-red-50 text-brand-red' },
    { id: 'improvements', label: 'Improvements',  count: improvements.length, badgeClass: 'bg-green-100 text-green-600' },
    { id: 'delays',       label: 'Delays',        count: delays.length,       badgeClass: 'bg-orange-100 text-orange-600' },
    ...(!subtaskId ? [{ id: 'revisions', label: 'Revisions', count: snapshots.length, badgeClass: 'bg-gray-100 text-gray-600' }] : []),
  ]

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40 no-print" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-[28rem] bg-white shadow-2xl z-50 flex flex-col no-print">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-0.5">{subtaskId ? 'Task History' : 'Edit History'}</div>
            <h2 className="text-base font-semibold text-gray-900">{subtaskId ? (subtaskLabel || projectLabel) : projectLabel}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-5 overflow-x-auto">
          {TABS.map(({ id, label, count, badgeClass }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`py-2.5 mr-4 text-sm font-medium border-b-2 transition-colors flex-shrink-0 ${
                activeTab === id
                  ? 'border-brand-red text-brand-red'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              {label}
              {count != null && !loading && count > 0 && (
                <span className={`ml-1.5 text-xs rounded-full px-1.5 py-0.5 ${badgeClass}`}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-400">Loading…</div>

          ) : activeTab === 'history' ? (
            visibleEntries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2">
                <svg className="w-9 h-9 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm">No edit history yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {visibleEntries.map(entry => {
                  const style = ACTION_STYLE[entry.action] || ACTION_STYLE.updated
                  return (
                    <div key={entry.id} className="px-5 py-4">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}>
                          {style.label}
                        </span>
                        <span className="text-xs font-medium text-gray-700 capitalize">
                          {entry.entity_type}
                          {entry.entity_label ? ` · ${entry.entity_label}` : ''}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mb-2.5">
                        {entry.user_name || entry.user_email || 'Unknown'} · {fmtDateTime(entry.created_at)}
                      </p>
                      {entry.changed_fields?.length > 0 && (
                        <div className="space-y-2 bg-gray-50 rounded-lg p-3">
                          {entry.changed_fields.map((f, i) => (
                            <div key={i}>
                              <div className="text-xs font-medium text-gray-500 mb-1">{f.field}</div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-xs px-1.5 py-0.5 bg-red-50 text-red-600 rounded line-through">{String(f.from)}</span>
                                <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <span className="text-xs px-1.5 py-0.5 bg-green-50 text-green-700 rounded">{String(f.to)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )

          ) : activeTab === 'downtime' ? (
            <>
              {projectDowntimeSummary && (
                <div className="px-5 pt-4 pb-3 border-b border-gray-100">
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Project Downtime</div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex gap-4 mb-2">
                      <div className="flex-1">
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Baseline</div>
                        <div className="text-xs text-gray-600">{projectDowntimeSummary.initialDays} {projectDowntimeSummary.initialDays === 1 ? 'day' : 'days'}</div>
                      </div>
                    </div>
                    <div className="border-t border-dashed border-gray-200 mb-2" />
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <div className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${projectDowntimeSummary.delta > 0 ? 'text-orange-500' : projectDowntimeSummary.delta < 0 ? 'text-green-600' : 'text-gray-400'}`}>Current</div>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-xs font-semibold text-gray-800">{projectDowntimeSummary.finalDays} {projectDowntimeSummary.finalDays === 1 ? 'day' : 'days'}</span>
                          {projectDowntimeSummary.delta !== 0 && (
                            <span className={`text-[10px] font-semibold ${projectDowntimeSummary.delta > 0 ? 'text-orange-500' : 'text-green-600'}`}>
                              {projectDowntimeSummary.delta > 0 ? `+${projectDowntimeSummary.delta}` : projectDowntimeSummary.delta} {Math.abs(projectDowntimeSummary.delta) === 1 ? 'day' : 'days'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {downtime.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2">
                  <svg className="w-9 h-9 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm">No task downtime changes recorded</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {downtime.map(d => {
                    const delta = d.finalDays - d.initialDays
                    return (
                      <div key={d.id} className="px-5 py-3.5">
                        <div className="text-sm font-medium text-gray-800 mb-2">{d.label}</div>
                        <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2.5">
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Initial</div>
                            <div className="text-xs text-gray-600">{d.initialDays} {d.initialDays === 1 ? 'day' : 'days'}</div>
                          </div>
                          <ArrowRight />
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Actual</div>
                            <div className="text-xs font-semibold text-gray-800">{d.finalDays} {d.finalDays === 1 ? 'day' : 'days'}</div>
                          </div>
                          <span className={`text-xs font-bold tabular-nums ${delta > 0 ? 'text-orange-500' : 'text-green-600'}`}>
                            {delta > 0 ? `+${delta}` : delta} {Math.abs(delta) === 1 ? 'day' : 'days'}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>

          ) : activeTab === 'improvements' ? (
            <>
              {improvements.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2">
                  <svg className="w-9 h-9 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm">No schedule improvements recorded</p>
                </div>
              ) : (
                <>
                  {projectDateSummary && (
                    <div className="px-5 pt-4 pb-3 border-b border-gray-100">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Project Schedule</div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex gap-4 mb-2">
                          <div className="flex-1">
                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Initial Arrival</div>
                            <div className="text-xs text-gray-600">{fmtDate(projectDateSummary.initialArrival)}</div>
                          </div>
                          <div className="flex-1">
                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Initial Delivery</div>
                            <div className="text-xs text-gray-600">{fmtDate(projectDateSummary.initialDelivery)}</div>
                          </div>
                        </div>
                        <div className="border-t border-dashed border-gray-200 mb-2" />
                        <div className="flex gap-4">
                          <div className="flex-1">
                            <div className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-0.5">Final Arrival</div>
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-xs font-semibold text-gray-800">{fmtDate(projectDateSummary.finalArrival)}</span>
                              {(() => { const d = daysDelta(projectDateSummary.initialArrival, projectDateSummary.finalArrival); return d != null && d !== 0 ? <span className={`text-[10px] font-semibold ${d > 0 ? 'text-orange-500' : 'text-green-600'}`}>{d > 0 ? `+${d}d` : `${d}d`}</span> : null })()}
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-0.5">Final Delivery</div>
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-xs font-semibold text-gray-800">{fmtDate(projectDateSummary.finalDelivery)}</span>
                              {(() => { const d = daysDelta(projectDateSummary.initialDelivery, projectDateSummary.finalDelivery); return d != null && d !== 0 ? <span className={`text-[10px] font-semibold ${d > 0 ? 'text-orange-500' : 'text-green-600'}`}>{d > 0 ? `+${d}d` : `${d}d`}</span> : null })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="divide-y divide-gray-100">
                    {improvements.map(d => (
                      <SubtaskDateCard
                        key={d.id}
                        item={d}
                        accent="text-green-600"
                        bg="bg-green-50"
                        divider="border-green-200"
                      />
                    ))}
                  </div>
                </>
              )}
            </>

          ) : activeTab === 'revisions' ? (
            snapshots.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2">
                <svg className="w-9 h-9 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414A1 1 0 0120 8.414V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                </svg>
                <p className="text-sm">No revisions saved yet</p>
                <p className="text-xs text-gray-300">Recorded the first time a change is made each day</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {snapshots.map(snap => (
                  <div key={snap.snapshot_date} className="px-5 py-3 flex items-center justify-between gap-3">
                    <span className="text-sm font-mono text-gray-700">
                      {registration}_{fmtSnapshotDate(snap.snapshot_date)}
                    </span>
                    <button
                      onClick={() => onPrintSnapshot?.(snap)}
                      className="flex-shrink-0 flex items-center gap-1.5 text-xs font-medium text-brand-red hover:text-brand-red-dark transition-colors px-2.5 py-1.5 bg-red-50 hover:bg-red-100 rounded-lg"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Print / Export PDF
                    </button>
                  </div>
                ))}
              </div>
            )

          ) : (
            <>
              {projectDateSummary && (
                <div className="px-5 pt-4 pb-3 border-b border-gray-100">
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Project Schedule</div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex gap-4 mb-2">
                      <div className="flex-1">
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Initial Arrival</div>
                        <div className="text-xs text-gray-600">{fmtDate(projectDateSummary.initialArrival)}</div>
                      </div>
                      <div className="flex-1">
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Initial Delivery</div>
                        <div className="text-xs text-gray-600">{fmtDate(projectDateSummary.initialDelivery)}</div>
                      </div>
                    </div>
                    <div className="border-t border-dashed border-gray-200 mb-2" />
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <div className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-0.5">Final Arrival</div>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-xs font-semibold text-gray-800">{fmtDate(projectDateSummary.finalArrival)}</span>
                          {(() => { const d = daysDelta(projectDateSummary.initialArrival, projectDateSummary.finalArrival); return d != null && d !== 0 ? <span className={`text-[10px] font-semibold ${d > 0 ? 'text-orange-500' : 'text-green-600'}`}>{d > 0 ? `+${d}d` : `${d}d`}</span> : null })()}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-0.5">Final Delivery</div>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-xs font-semibold text-gray-800">{fmtDate(projectDateSummary.finalDelivery)}</span>
                          {(() => { const d = daysDelta(projectDateSummary.initialDelivery, projectDateSummary.finalDelivery); return d != null && d !== 0 ? <span className={`text-[10px] font-semibold ${d > 0 ? 'text-orange-500' : 'text-green-600'}`}>{d > 0 ? `+${d}d` : `${d}d`}</span> : null })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {delays.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2">
                  <svg className="w-9 h-9 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm">No subtask date delays recorded</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {delays.map(d => (
                    <SubtaskDateCard
                      key={d.id}
                      item={d}
                      accent="text-orange-500"
                      bg="bg-gray-50"
                      divider="border-gray-200"
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}
